import { FormField } from '@sunwebgroup/uds-ui/fields/form-field'
import { RadioButtonField as UdsRadioButtonField } from '@sunwebgroup/uds-ui/fields/radio-button-field'
import { RadioButtonGroup } from '@sunwebgroup/uds-ui/radio-button'
import * as React from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useIntl } from '@/intl'
import { getErrorMessage } from '@/scopes/common/components/FormElements/getErrorMessage'
import { useStatus } from '@/scopes/common/components/FormElements/useStatus'
import styles from './RadioButtonInput.module.css'

export interface RadioButtonFieldProps {
  items: { value: string, label: string }[]
  label?: string
  name: string
  required?: boolean
  requiredErrorMessage?: string
  helpText?: string
  disabled?: boolean
}

export function RadioButtonInput({
  items,
  label,
  name,
  required,
  requiredErrorMessage,
  helpText,
  disabled,
}: RadioButtonFieldProps) {
  const intl = useIntl()
  const {
    control,
    formState,
  } = useFormContext()
  const { errors } = formState

  const status = useStatus({
    name,
    required,
    shouldMarkValidStatus: value => Boolean(value),
  })
  const errorMessage = getErrorMessage(name, errors, { requiredErrorMessage })

  const { field } = useController({
    name,
    control,
    disabled,
    rules: {
      required,
    },
  })

  return (
    <FormField
      label={label}
      feedbackText={errorMessage}
      status={status}
      optionalLabel={!required ? intl.formatMessage({ id: 'common_form_field_optional_label' }) : undefined}
      helpText={helpText}
      disabled={disabled}
    >
      <div className={helpText ? styles.helpTextSpacing : undefined}>
        <RadioButtonGroup
          value={field.value ?? ''}
          onValueChange={field.onChange}
          onBlur={field.onBlur}
          name={name}
          disabled={field.disabled}
          className={styles.radioButtonGroup}
        >
          {items.map(({ value, label: itemLabel }) => (
            <UdsRadioButtonField
              key={`${name}-${value}`}
              value={value}
              label={itemLabel}
              variant="outlined"
              disabled={disabled}
            />
          ))}
        </RadioButtonGroup>
      </div>
    </FormField>
  )
}
